import { EdgeResizer } from '~src/utils/EdgeResizer';

const BACKGROUND_KINDS = [
  'graphic_frame',
  'sky_clouds',
  'nature_landscape',
  'abstract_gradient',
  'neutral_light',
  'unknown',
];
const PERSON_PLACEMENT_SCALE_BY_BACKGROUND_KIND = {
  graphic_frame: 0.5,
};
const EDGE_ANCHOR_THRESHOLD_RATIO = 0.12;
const MAGIC_FRAME_SUBJECT_KIND = 'magic_frame';

const roundPayloadNumber = (value, decimals = 4) => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return 0;
  }

  const factor = Math.pow(10, decimals);
  return Math.round(numericValue * factor) / factor;
};

const toPositiveNumber = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue > 0
    ? numericValue
    : null;
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const getObjectOriginOffset = (origin, size) => {
  if (origin === 'center') {
    return size / 2;
  }

  if (origin === 'right' || origin === 'bottom') {
    return size;
  }

  return 0;
};

const getEdgeAnchor = ({ start, size, canvasSize, startAnchor, endAnchor }) => {
  const startDistance = Math.max(0, start);
  const endDistance = Math.max(0, canvasSize - (start + size));
  const threshold = canvasSize * EDGE_ANCHOR_THRESHOLD_RATIO;

  if (startDistance <= threshold && startDistance <= endDistance) {
    return startAnchor;
  }

  if (endDistance <= threshold && endDistance < startDistance) {
    return endAnchor;
  }

  return 'center';
};

const getAnchoredStart = ({ start, size, targetSize, anchor }) => {
  if (anchor === 'left' || anchor === 'top') {
    return start;
  }

  if (anchor === 'right' || anchor === 'bottom') {
    return start + size - targetSize;
  }

  return start + (size - targetSize) / 2;
};

const getUrlKey = (url) => {
  if (!url) return '';

  try {
    const parsedUrl = new URL(url);
    return decodeURIComponent(parsedUrl.pathname.split('/').pop() || '');
  } catch (error) {
    return '';
  }
};

export const normalizeBackgroundUrl = (url) => {
  if (!url) return '';

  try {
    const parsedUrl = new URL(url);
    return `${parsedUrl.origin}${parsedUrl.pathname}`;
  } catch (error) {
    return String(url);
  }
};

const urlsMatch = (leftUrl, rightUrl) => {
  const left = normalizeBackgroundUrl(leftUrl);
  const right = normalizeBackgroundUrl(rightUrl);

  if (!left || !right) return false;
  if (left === right) return true;

  const leftKey = getUrlKey(leftUrl);
  const rightKey = getUrlKey(rightUrl);

  return Boolean(leftKey && rightKey && leftKey === rightKey);
};

const flattenSidebarBackgrounds = (sidebarBackgrounds = {}) =>
  Object.values(sidebarBackgrounds).flatMap((group) => group?.items || []);

export const findBackgroundRecordByUrl = (sidebarBackgrounds, backgroundUrl) =>
  flattenSidebarBackgrounds(sidebarBackgrounds).find((item) =>
    urlsMatch(item?.file?.url, backgroundUrl)
  );

const extractTagNames = (record) =>
  (record?.tags || [])
    .map((tag) => tag?.name)
    .filter(Boolean)
    .map((tagName) => String(tagName).toLowerCase());

const includesAny = (haystack, needles) =>
  needles.some((needle) => haystack.includes(needle));

export const classifyBackgroundKind = ({
  explicitKind,
  tags = [],
  url = '',
} = {}) => {
  if (BACKGROUND_KINDS.includes(explicitKind)) {
    return explicitKind;
  }

  const haystack = [...tags, url].join(' ').toLowerCase();

  if (
    includesAny(haystack, [
      'cornice',
      'frame',
      'bordo',
      'border',
      'prelapide',
      'lapide',
      'manifesto',
      'biglietto',
      'ringraziamento',
      'ricordino',
    ])
  ) {
    return 'graphic_frame';
  }

  if (
    includesAny(haystack, [
      'ciel',
      'sky',
      'nuvol',
      'cloud',
      'alba',
      'tramonto',
      'sunset',
      'celest',
      'paradis',
    ])
  ) {
    return 'sky_clouds';
  }

  if (
    includesAny(haystack, [
      'natura',
      'paesagg',
      'landscape',
      'bosco',
      'forest',
      'prato',
      'montagn',
      'mare',
      'sea',
      'seascape',
      'garden',
    ])
  ) {
    return 'nature_landscape';
  }

  if (
    includesAny(haystack, [
      'astratt',
      'abstract',
      'gradient',
      'gradiente',
      'sfumat',
      'pastello',
      'texture',
      'pattern',
    ])
  ) {
    return 'abstract_gradient';
  }

  if (
    includesAny(haystack, [
      'avorio',
      'crema',
      'chiaro',
      'luce',
      'light',
      'neutral',
      'neutro',
      'perla',
    ])
  ) {
    return 'neutral_light';
  }

  return 'unknown';
};

export const getBackgroundDescriptor = ({
  backgroundImage,
  backgroundUrl,
  editorConfig,
  fallbackKind = 'unknown',
  fallbackSource = 'canvas_background',
} = {}) => {
  const url =
    backgroundUrl ||
    (typeof backgroundImage?.getSrc === 'function'
      ? backgroundImage.getSrc()
      : '');
  const record = findBackgroundRecordByUrl(
    editorConfig?.sidebarBackgrounds,
    url
  );
  const tags = extractTagNames(record);
  const recordKind = classifyBackgroundKind({ tags, url });
  const explicitKind =
    backgroundImage?.giotto?.backgroundKind ||
    backgroundImage?.giotto?.backgroundFusion?.background?.kind;
  const explicitClassifiedKind = classifyBackgroundKind({ explicitKind });
  const classifiedKind =
    recordKind !== 'unknown' || record ? recordKind : explicitClassifiedKind;
  const kind =
    classifiedKind === 'unknown' && fallbackKind !== 'unknown'
      ? fallbackKind
      : classifiedKind;

  let source = fallbackSource;
  if (record?.isGlobal === true) {
    source = 'library_default';
  } else if (record) {
    source = 'library_custom';
  } else if (url.includes('ai-background')) {
    source = 'ai_generated';
  }

  return {
    kind,
    source,
    tags,
  };
};

export const getObjectPlacement = (object, canvasDimensions) => {
  const canvasWidth = toPositiveNumber(canvasDimensions?.width);
  const canvasHeight = toPositiveNumber(canvasDimensions?.height);

  if (!canvasWidth || !canvasHeight) {
    throw new Error('Canvas dimensions are required for background fusion');
  }

  if (typeof object?.setCoords === 'function') {
    object.setCoords();
  }

  const rect =
    typeof object?.getBoundingRect === 'function'
      ? object.getBoundingRect(true, true)
      : object;
  const width = toPositiveNumber(rect?.width);
  const height = toPositiveNumber(rect?.height);
  const left =
    typeof object?.getBoundingRect === 'function'
      ? Number(rect?.left)
      : Number(rect?.left) - getObjectOriginOffset(object?.originX, width || 0);
  const top =
    typeof object?.getBoundingRect === 'function'
      ? Number(rect?.top)
      : Number(rect?.top) - getObjectOriginOffset(object?.originY, height || 0);

  if (!Number.isFinite(left) || !Number.isFinite(top) || !width || !height) {
    throw new Error('Selected image placement is not available');
  }

  return {
    left: Math.round(left),
    top: Math.round(top),
    width: Math.round(width),
    height: Math.round(height),
    centerXRatio: roundPayloadNumber((left + width / 2) / canvasWidth),
    centerYRatio: roundPayloadNumber((top + height / 2) / canvasHeight),
    widthRatio: roundPayloadNumber(width / canvasWidth),
    heightRatio: roundPayloadNumber(height / canvasHeight),
    angle: roundPayloadNumber(object?.angle || 0, 2),
  };
};

export const getPersonPlacementScale = (background) => {
  const scale = PERSON_PLACEMENT_SCALE_BY_BACKGROUND_KIND[background?.kind];

  return toPositiveNumber(scale) || 1;
};

export const getSubjectDescriptor = (selection) => {
  if (selection?.type === 'MagicFrame') {
    return {
      kind: MAGIC_FRAME_SUBJECT_KIND,
      frameShape: 'rectangular_decorative_border',
    };
  }

  return {
    kind: 'image',
  };
};

export const getPersonPlacementAnchor = ({ placement, canvas }) => {
  const canvasWidth = toPositiveNumber(canvas?.width);
  const canvasHeight = toPositiveNumber(canvas?.height);

  if (!canvasWidth || !canvasHeight) {
    return 'center-center';
  }

  const horizontal = getEdgeAnchor({
    start: placement.left,
    size: placement.width,
    canvasSize: canvasWidth,
    startAnchor: 'left',
    endAnchor: 'right',
  });
  const vertical = getEdgeAnchor({
    start: placement.top,
    size: placement.height,
    canvasSize: canvasHeight,
    startAnchor: 'top',
    endAnchor: 'bottom',
  });

  return `${vertical}-${horizontal}`;
};

export const applyPersonPlacementCompensation = ({
  placement,
  canvas,
  background,
}) => {
  const canvasWidth = toPositiveNumber(canvas?.width);
  const canvasHeight = toPositiveNumber(canvas?.height);
  const scale = getPersonPlacementScale(background);

  if (!canvasWidth || !canvasHeight || scale >= 1) {
    return placement;
  }

  const width = Math.max(1, Math.round(placement.width * scale));
  const height = Math.max(1, Math.round(placement.height * scale));
  const anchor = getPersonPlacementAnchor({ placement, canvas });
  const [verticalAnchor, horizontalAnchor] = anchor.split('-');
  const left = clamp(
    Math.round(
      getAnchoredStart({
        start: placement.left,
        size: placement.width,
        targetSize: width,
        anchor: horizontalAnchor,
      })
    ),
    0,
    Math.max(0, Math.round(canvasWidth - width))
  );
  const top = clamp(
    Math.round(
      getAnchoredStart({
        start: placement.top,
        size: placement.height,
        targetSize: height,
        anchor: verticalAnchor,
      })
    ),
    0,
    Math.max(0, Math.round(canvasHeight - height))
  );

  return {
    ...placement,
    left,
    top,
    width,
    height,
    centerXRatio: roundPayloadNumber((left + width / 2) / canvasWidth),
    centerYRatio: roundPayloadNumber((top + height / 2) / canvasHeight),
    widthRatio: roundPayloadNumber(width / canvasWidth),
    heightRatio: roundPayloadNumber(height / canvasHeight),
  };
};

export const buildBackgroundFusionLayout = ({
  canvasWidth,
  canvasHeight,
  selection,
  background,
}) => {
  const canvas = {
    width: Math.round(canvasWidth),
    height: Math.round(canvasHeight),
  };
  const sourcePerson = getObjectPlacement(selection, {
    width: canvasWidth,
    height: canvasHeight,
  });
  const resolvedBackground = background || {
    kind: 'unknown',
    source: 'canvas_background',
    tags: [],
  };
  const person = applyPersonPlacementCompensation({
    placement: sourcePerson,
    canvas,
    background: resolvedBackground,
  });
  const placementScale = getPersonPlacementScale(resolvedBackground);
  const placementAnchor = getPersonPlacementAnchor({
    placement: sourcePerson,
    canvas,
  });

  return {
    mode: 'compositionGuide',
    canvas,
    person,
    subject: getSubjectDescriptor(selection),
    background: resolvedBackground,
    placementPolicy:
      placementScale < 1
        ? 'compensate_graphic_frame_overfill'
        : 'preserve_bbox',
    ...(placementScale < 1
      ? {
          sourcePerson,
          placementCompensation: {
            scale: placementScale,
            anchor: placementAnchor,
            reason: 'graphic_frame_model_overfill',
          },
        }
      : {}),
  };
};

export const getScaledPersonReferenceUrl = (personUrl, placement) => {
  const width = Math.round(Number(placement?.width || 0));
  const height = Math.round(Number(placement?.height || 0));

  if (!personUrl || width <= 0 || height <= 0) {
    return personUrl;
  }

  try {
    return new EdgeResizer(personUrl).resize(width, height, 'contain');
  } catch (error) {
    return personUrl;
  }
};
